import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Tag, DollarSign, Percent, Users, Copy, X, Check, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PromoCodeAdmin() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [codes, setCodes] = useState([]);
  const [stats, setStats] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ code: '', type: 'fixed', discount_amount: '', description: '', min_order: '', max_uses: '', referrer_name: '', referrer_email: '', referrer_reward: '', applicable_cities: [], expires_at: '', is_public: false });

  useEffect(() => {
    if (user?.role !== 'admin') { navigate('/'); return; }
    fetchData();
  }, []);

  const fetchData = async () => {
    const [codesRes, statsRes] = await Promise.all([
      axios.get(`${API}/promo-codes`, { headers }),
      axios.get(`${API}/promo-codes/stats`, { headers }),
    ]);
    setCodes(codesRes.data.codes || []);
    setStats(statsRes.data);
  };

  const saveCode = async () => {
    if (!form.code) { toast.error('Code is required'); return; }
    try {
      const data = { ...form, discount_amount: parseFloat(form.discount_amount) || 0, min_order: parseFloat(form.min_order) || 0, max_uses: parseInt(form.max_uses) || 0, referrer_reward: parseFloat(form.referrer_reward) || 0 };
      if (editingId) {
        await axios.put(`${API}/promo-codes/${editingId}`, data, { headers });
        toast.success('Code updated');
      } else {
        await axios.post(`${API}/promo-codes`, data, { headers });
        toast.success('Code created');
      }
      setShowForm(false); setEditingId(null);
      setForm({ code: '', type: 'fixed', discount_amount: '', description: '', min_order: '', max_uses: '', referrer_name: '', referrer_email: '', referrer_reward: '', applicable_cities: [], expires_at: '', is_public: false });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const deleteCode = async (id) => {
    if (!confirm('Delete this promo code?')) return;
    await axios.delete(`${API}/promo-codes/${id}`, { headers });
    toast.success('Deleted'); fetchData();
  };

  const toggleActive = async (code) => {
    await axios.put(`${API}/promo-codes/${code.code_id}`, { is_active: !code.is_active }, { headers });
    fetchData();
  };

  const copyCode = (code) => { navigator.clipboard.writeText(code).then(() => toast.success(`${code} copied!`)).catch(() => toast.error('Copy failed')); };

  const CITIES = [{ key: 'denver', label: 'Denver' }, { key: 'las_vegas', label: 'Las Vegas' }, { key: 'los_angeles', label: 'Los Angeles' }, { key: 'charlotte', label: 'Charlotte' }];

  return (
    <div className="min-h-screen bg-background pt-16 pb-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between mb-6 pt-4">
          <div>
            <h1 className="text-2xl font-bold">Promo & Referral Codes</h1>
            <p className="text-sm text-muted-foreground">Create discount codes, referral rewards, and track usage</p>
          </div>
          <Button onClick={() => { setShowForm(true); setEditingId(null); setForm({ code: '', type: 'fixed', discount_amount: '', description: '', min_order: '', max_uses: '', referrer_name: '', referrer_email: '', referrer_reward: '', applicable_cities: [], expires_at: '', is_public: false }); }} data-testid="new-promo-btn">
            <Plus className="h-4 w-4 mr-1" /> New Code
          </Button>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            <Card className="border-border"><CardContent className="p-4 text-center"><p className="text-2xl font-bold">{stats.total_codes}</p><p className="text-xs text-muted-foreground">Total Codes</p></CardContent></Card>
            <Card className="border-border"><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-emerald-500">{stats.active_codes}</p><p className="text-xs text-muted-foreground">Active</p></CardContent></Card>
            <Card className="border-border"><CardContent className="p-4 text-center"><p className="text-2xl font-bold">{stats.total_redemptions}</p><p className="text-xs text-muted-foreground">Redemptions</p></CardContent></Card>
            <Card className="border-border"><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-indigo-500">${stats.total_discount_given}</p><p className="text-xs text-muted-foreground">Discount Given</p></CardContent></Card>
          </div>
        )}

        {/* Create/Edit Form */}
        {showForm && (
          <Card className="mb-6 border-indigo-500/20">
            <CardContent className="p-5 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold">{editingId ? 'Edit Code' : 'New Promo Code'}</h3>
                <Button variant="ghost" size="icon" onClick={() => setShowForm(false)}><X className="h-4 w-4" /></Button>
              </div>
              <div className="grid sm:grid-cols-3 gap-3">
                <div className="space-y-1"><Label className="text-xs">Code *</Label><Input value={form.code} onChange={e => setForm(p => ({ ...p, code: e.target.value.toUpperCase() }))} placeholder="STEVE100" className="font-mono" data-testid="promo-code-input" /></div>
                <div className="space-y-1"><Label className="text-xs">Type</Label>
                  <select value={form.type} onChange={e => setForm(p => ({ ...p, type: e.target.value }))} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                    <option value="fixed">Fixed ($)</option><option value="percent">Percent (%)</option><option value="referral">Referral</option>
                  </select>
                </div>
                <div className="space-y-1"><Label className="text-xs">Discount Amount</Label><Input type="number" value={form.discount_amount} onChange={e => setForm(p => ({ ...p, discount_amount: e.target.value }))} placeholder={form.type === 'percent' ? '20' : '100'} /></div>
              </div>
              <div className="grid sm:grid-cols-3 gap-3">
                <div className="space-y-1"><Label className="text-xs">Description</Label><Input value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="$100 off first event" /></div>
                <div className="space-y-1"><Label className="text-xs">Min Order ($)</Label><Input type="number" value={form.min_order} onChange={e => setForm(p => ({ ...p, min_order: e.target.value }))} placeholder="0" /></div>
                <div className="space-y-1"><Label className="text-xs">Max Uses (0=unlimited)</Label><Input type="number" value={form.max_uses} onChange={e => setForm(p => ({ ...p, max_uses: e.target.value }))} placeholder="0" /></div>
              </div>
              {form.type === 'referral' && (
                <div className="grid sm:grid-cols-3 gap-3 p-3 rounded-lg bg-muted/30 border border-border">
                  <div className="space-y-1"><Label className="text-xs">Referrer Name</Label><Input value={form.referrer_name} onChange={e => setForm(p => ({ ...p, referrer_name: e.target.value }))} /></div>
                  <div className="space-y-1"><Label className="text-xs">Referrer Email</Label><Input value={form.referrer_email} onChange={e => setForm(p => ({ ...p, referrer_email: e.target.value }))} /></div>
                  <div className="space-y-1"><Label className="text-xs">Referrer Reward ($)</Label><Input type="number" value={form.referrer_reward} onChange={e => setForm(p => ({ ...p, referrer_reward: e.target.value }))} placeholder="50" /></div>
                </div>
              )}
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="space-y-1"><Label className="text-xs">Expires (optional)</Label><Input type="date" value={form.expires_at} onChange={e => setForm(p => ({ ...p, expires_at: e.target.value }))} /></div>
                <div className="space-y-1"><Label className="text-xs">City Restrictions (empty = all)</Label>
                  <div className="flex gap-1 flex-wrap">{CITIES.map(c => (
                    <Button key={c.key} size="sm" variant={form.applicable_cities.includes(c.key) ? 'default' : 'outline'} className="text-xs h-7" onClick={() => setForm(p => ({ ...p, applicable_cities: p.applicable_cities.includes(c.key) ? p.applicable_cities.filter(x => x !== c.key) : [...p.applicable_cities, c.key] }))}>{c.label}</Button>
                  ))}</div>
                </div>
              </div>
              <label className="flex items-center gap-2 text-xs font-medium cursor-pointer" data-testid="promo-public-toggle">
                <input type="checkbox" checked={!!form.is_public} onChange={e => setForm(p => ({ ...p, is_public: e.target.checked }))} className="h-4 w-4 accent-fuchsia-500" />
                Show on the public /deals page
              </label>
              <Button onClick={saveCode} data-testid="save-promo">{editingId ? 'Update' : 'Create Code'}</Button>
            </CardContent>
          </Card>
        )}

        {/* Codes List */}
        <div className="space-y-3" data-testid="promo-list">
          {codes.map(c => (
            <Card key={c.code_id} className={`border-border ${!c.is_active ? 'opacity-50' : ''}`}>
              <CardContent className="p-4 flex items-center gap-4">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${c.type === 'referral' ? 'bg-purple-500/10' : c.type === 'percent' ? 'bg-amber-500/10' : 'bg-emerald-500/10'}`}>
                  {c.type === 'referral' ? <Users className="h-5 w-5 text-purple-400" /> : c.type === 'percent' ? <Percent className="h-5 w-5 text-amber-400" /> : <DollarSign className="h-5 w-5 text-emerald-400" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-bold text-sm">{c.code}</span>
                    <Badge variant="outline" className="text-[9px]">{c.type}</Badge>
                    <Badge className={c.is_active ? 'bg-emerald-500' : 'bg-slate-400'}>{c.is_active ? 'Active' : 'Inactive'}</Badge>
                    {c.is_public && <Badge className="bg-fuchsia-500">Public deal</Badge>}
                  </div>
                  <p className="text-xs text-muted-foreground">{c.description} {c.type === 'percent' ? `(${c.discount_amount}% off)` : `($${c.discount_amount} off)`}</p>
                  <p className="text-[10px] text-muted-foreground">Used {c.times_used}x | ${c.total_discount_given} given{c.applicable_cities?.length > 0 ? ` | ${c.applicable_cities.join(', ')}` : ''}{c.referrer_name ? ` | Referrer: ${c.referrer_name}` : ''}</p>
                </div>
                <div className="flex items-center gap-1">
                  <Button size="sm" variant="ghost" onClick={() => copyCode(c.code)} title="Copy"><Copy className="h-3.5 w-3.5" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => toggleActive(c)} title={c.is_active ? 'Deactivate' : 'Activate'}>{c.is_active ? <X className="h-3.5 w-3.5" /> : <Check className="h-3.5 w-3.5" />}</Button>
                  <Button size="sm" variant="ghost" onClick={() => { setEditingId(c.code_id); setForm({ code: c.code, type: c.type, discount_amount: c.discount_amount, description: c.description || '', min_order: c.min_order || '', max_uses: c.max_uses || '', referrer_name: c.referrer_name || '', referrer_email: c.referrer_email || '', referrer_reward: c.referrer_reward || '', applicable_cities: c.applicable_cities || [], expires_at: c.expires_at || '', is_public: !!c.is_public }); setShowForm(true); }}><Edit className="h-3.5 w-3.5" /></Button>
                  <Button size="sm" variant="ghost" className="text-destructive" onClick={() => deleteCode(c.code_id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
